import { useMemo, useContext } from "react";

import { useHome } from "../model/useHome";

import { TenantContext } from "../templates/context/TenantContext";

import { i18n } from "../translate/i18n";

import { ROUTES_APPLICATION_PATH } from "../routes.constants";
import { getNavigationOptionsByTenanat } from "./strategy/menu/NavigationOptions/navigationOptions.strategy";
import { SECTION_LABELS } from "./strategy/menu/NavigationOptions/utils";

import { createDecimalFormatter, textFormatter } from "../utils/formatter/formatter";

const { t } = i18n;

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const MODULE_LABELS = {
  product: 'Produto',
  assortment: 'Sortimento',
  inventory: 'Abastecimento',
  channels: 'Canais',
  pricing: 'Preço',
};

const IMPACT_LEVELS = {
  alto: { label: 'Impacto alto', severity: 'high' },
  medio: { label: 'Impacto médio', severity: 'medium' },
  baixo: { label: 'Impacto baixo', severity: 'low' },
};

// módulos sem dados na API ainda — renderizados zerados e com blur
const PLACEHOLDER_MODULE_CARDS = [
  { key: 'assortment', description: 'Qualificação de MIX, novo mix sugerido e curadoria por porte de loja' },
  { key: 'inventory', description: 'Distribuição, reposição e qualidade de estoque na malha de lojas' },
  { key: 'channels', description: 'Performance por loja e canal, com plano de ação por praça' },
];

// a API mistura "12,80" (pt-BR) e "22290000.00" (en-US)
const toNumber = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value;
  const normalized = value.includes(',')
    ? value.replace(/\./g, '').replace(',', '.')
    : value;
  return Number(normalized) || 0;
};

const oneDecimal = createDecimalFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
const noDecimal = createDecimalFormatter();

export const formatCompactCurrency = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `R$ ${oneDecimal.format(value / 1_000_000)}M`;
  if (abs >= 1_000) return `R$ ${noDecimal.format(value / 1_000)}k`;
  return `R$ ${noDecimal.format(value)}`;
};

export const formatFullCurrency = (value) => `R$ ${noDecimal.format(value)}`;

export const formatPercent = (ratio, { decimals = 0 } = {}) =>
  `${createDecimalFormatter({ minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(ratio * 100)}%`;

const GREETING = 'Bom dia';

const normalizeImpact = (impact) =>
  String(impact ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// impact pode vir como nível ("alto") ou como valor monetário (1200000 / "1200000.00")
const getImpact = (impact) => {
  const level = IMPACT_LEVELS[normalizeImpact(impact)];
  if (level) return level;

  const value = toNumber(typeof impact === 'number' ? impact : String(impact ?? ''));
  if (value) return { label: `Impacto ${formatCompactCurrency(value)}`, severity: 'medium' };

  return { label: 'Impacto', severity: 'low' };
};

const getOpportunitiesRoute = (tenantContext) => {
  const fallback = ROUTES_APPLICATION_PATH.OPPORTUNITIES.COMPONENT_URL[2];
  if (!tenantContext?.tenant) return fallback;

  const menus = getNavigationOptionsByTenanat(tenantContext.tenant, t, tenantContext) ?? [];
  const opportunitySection = menus
    .flatMap(menu => menu.sections ?? [])
    .find(section => section.label === SECTION_LABELS.OPPORTUNITIES && section.items.length);

  return opportunitySection?.items[0]?.route ?? fallback;
};

export const useHomeViewModel = ({ shouldFetchHome = true } = {}) => {
  const tenantContext = useContext(TenantContext);

  const { home } = useHome({ shouldFetchHome });
  const { earnings, cards, aiRecommendations } = home.data ?? {};

  const opportunitiesRoute = useMemo(() => getOpportunitiesRoute(tenantContext), [tenantContext]);

  // mesmo nome de empresa exibido no rodapé do sidebar
  const greeting = useMemo(() => {
    const companyName = tenantContext?.tenant ? textFormatter(tenantContext.tenant) : '';
    return companyName ? `${GREETING}, ${companyName}` : GREETING;
  }, [tenantContext?.tenant]);

  const earningsChart = useMemo(() => {
    const months = (earnings?.earningsFromLastSemester ?? []).map(item => ({
      label: MONTH_LABELS[item.month - 1],
      year: item.year,
      monthlyReturn: toNumber(item.monthlyReturn),
      opportunityQuality: toNumber(item.opportunityQuality),
    }));

    return {
      data: months,
      semesterVariation: toNumber(earnings?.semesterVariation) / 100,
    };
  }, [earnings]);

  const expectedReturn = useMemo(() => {
    const total = toNumber(earnings?.expectedReturn);
    const capturedPercent = toNumber(earnings?.capturedPercent) / 100;

    return {
      total,
      capturedPercent,
      capturedValue: total * capturedPercent,
    };
  }, [earnings]);

  const moduleCards = useMemo(() => {
    const opportunities = cards?.opportunities ?? {};

    // blur somente quando a API não enviar os dados do módulo
    const productCard = {
      key: 'product',
      title: MODULE_LABELS.product,
      description: 'Ajustes de plano, OTB e forecast que evitam ruptura e estoque parado',
      totalValue: toNumber(opportunities.totalOfOpportunitiesValue),
      capturedValue: toNumber(opportunities.finishedOpportunitiesValue),
      totalOfOpportunities: toNumber(opportunities.totalOfOpportunities),
      capturedPercent: toNumber(opportunities.finishedOpportunitiesPercent) / 100,
      isAvailable: Boolean(cards?.opportunities),
      route: opportunitiesRoute,
      // TODO: a API do /home ainda não envia os destaques por módulo
      highlights: opportunities.highlights ?? [],
    };

    const placeholders = PLACEHOLDER_MODULE_CARDS.map(card => ({
      ...card,
      title: MODULE_LABELS[card.key],
      totalValue: 0,
      capturedValue: 0,
      totalOfOpportunities: 0,
      capturedPercent: 0,
      isAvailable: false,
      route: null,
      highlights: [],
    }));

    return [productCard, ...placeholders];
  }, [cards, opportunitiesRoute]);

  const summary = useMemo(() => {
    const opportunities = cards?.opportunities ?? {};
    const openValue = toNumber(opportunities.totalOfOpportunitiesValue) - toNumber(opportunities.finishedOpportunitiesValue);

    return {
      totalOfOpportunities: toNumber(opportunities.totalOfOpportunities),
      openValue: Math.max(openValue, 0),
    };
  }, [cards]);

  const recommendations = useMemo(() =>
    (aiRecommendations?.recommendations ?? []).map(item => {
      const impact = getImpact(item.impact);

      return {
        id: `${item.module}-${item.productId}`,
        productId: item.productId,
        title: item.title,
        description: item.description,
        moduleLabel: MODULE_LABELS[item.module] ?? item.module,
        impactLabel: impact.label,
        severity: impact.severity,
        route: opportunitiesRoute,
      };
    }),
  [aiRecommendations, opportunitiesRoute]);

  return {
    actions: {},
    ctx: {
      greeting,
      summary,
      earningsChart,
      expectedReturn,
      moduleCards,
      recommendations,
      productWithGreatestOpportunityValue: aiRecommendations?.productWithGreatestOpportunityValue,
      userId: tenantContext?.userInfo?.userId,
      tenantId: tenantContext?.userInfo?.tenant?.tenantId,
    },
    uiState: {
      isLoadingHome: home.isLoading,
      homeError: home.error,
    },
  };
};
